"use client";

import React from "react";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { Badge } from "@/components/ui/badge";
import { User as UserIcon, Mail, Shield, Calendar } from "lucide-react";
import { User } from "@/lib/types/user";

interface UserDisplayPopoverProps {
  user: User;
  children: React.ReactNode;
}

const roleOptions = [
  { value: "wtc_admin", label: "WTC Admin" },
  { value: "wtc_employee", label: "WTC Employee" },
  { value: "client_user", label: "Client User" },
];

export const UserDisplayPopover: React.FC<UserDisplayPopoverProps> = ({ user, children }) => {
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const displayName = fullName || user.email;
  const roleLabel = roleOptions.find((r) => r.value === user.role)?.label || user.role;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const popoverContent = (
    <div className="space-y-4">
      {/* Header with avatar and name */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
          <span className="text-lg font-medium text-blue-800 dark:text-blue-200">
            {(user.first_name?.[0] || "") + (user.last_name?.[0] || "") ||
              user.email?.[0]?.toUpperCase() ||
              "?"}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-card-foreground truncate">{displayName}</h3>
          <p className="text-sm text-gray-500 truncate">{user.email}</p>
        </div>
      </div>

      {/* User details */}
      <div className="space-y-3">
        {/* Role */}
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Role:</span>
          <Badge variant={user.role === "wtc_admin" ? "default" : "secondary"} className="text-xs">
            {roleLabel}
          </Badge>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${user.is_active ? "bg-green-500" : "bg-red-500"}`}
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">{user.is_active ? "Active" : "Inactive"}</span>
        </div>

        {/* Created date */}
        {user.created_at && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Joined: {formatDate(user.created_at)}</span>
          </div>
        )}

        {/* Last login - removed as last_login is not available in User type */}

        {/* Email verification */}
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Email: {user.email_verified ? "Verified" : "Unverified"}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <ResponsivePopover
      content={popoverContent}
      title="User Information"
      className="w-80 z-[9998]"
      align="center"
      popoverSide="bottom"
      drawerSide="bottom"
    >
      {children}
    </ResponsivePopover>
  );
};
